import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import TopNav from "../components/TopNav";
import { Button } from "../components/ui/button";
import { Printer, ArrowLeft } from "@phosphor-icons/react";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function Export() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [entries, setEntries] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(""); // "" = whole year
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      const params = { year: Number(year) };
      if (month !== "") params.month = Number(month) + 1;
      const r = await api.get("/entries", { params });
      // Oldest first for reading order in the book
      setEntries([...r.data].sort((a, b) =>
        (a.entry_date || "").localeCompare(b.entry_date || "")
      ));
    } catch {
      navigate("/", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const availableYears = useMemo(() => {
    const now = new Date().getFullYear();
    return [now, now - 1, now - 2, now - 3, now - 4];
  }, []);

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;

  const title = month === "" ? `${year}` : `${MONTHS[month]} ${year}`;

  return (
    <div className="min-h-screen paper-grain" data-testid="export-page">
      <div className="no-print"><TopNav user={user} onLogout={async () => { await api.post("/auth/logout"); navigate("/"); }} /></div>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <button
          data-testid="back-to-dashboard"
          onClick={() => navigate("/dashboard")}
          className="no-print text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground hover:text-foreground flex items-center gap-2 mb-10"
        >
          <ArrowLeft size={14} /> Back
        </button>

        <div className="no-print">
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Export</p>
          <h1 className="font-serif text-5xl lg:text-6xl font-light tracking-tighter mt-3 mb-8">
            Turn a month or year into a <span className="italic text-accent">keepsake</span>
          </h1>

          <div className="border border-border rounded-lg bg-card p-6 mb-10 grid sm:grid-cols-[1fr_1fr_auto] gap-4 items-end">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground block mb-2">Year</label>
              <select
                data-testid="export-year"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="w-full bg-transparent border border-border rounded-lg px-3 py-2 font-serif text-lg"
              >
                {availableYears.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground block mb-2">Month</label>
              <select
                data-testid="export-month"
                value={month}
                onChange={(e) => setMonth(e.target.value === "" ? "" : Number(e.target.value))}
                className="w-full bg-transparent border border-border rounded-lg px-3 py-2 font-serif text-lg"
              >
                <option value="">Whole year</option>
                {MONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}
              </select>
            </div>
            <Button
              data-testid="print-btn"
              onClick={() => window.print()}
              disabled={entries.length === 0}
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-11"
            >
              <Printer size={16} className="mr-2" /> Print / Save PDF
            </Button>
          </div>

          <p className="text-sm text-muted-foreground mb-8" data-testid="export-count">
            {entries.length} {entries.length === 1 ? "entry" : "entries"} in {title}.
            {entries.length > 0 && " Click Print to save as PDF via your browser (choose 'Save as PDF' as the destination)."}
          </p>
        </div>

        {/* Printable content */}
        <article className="print-book" data-testid="print-book">
          <header className="text-center mb-16 print-entry">
            <p className="text-xs uppercase tracking-[0.4em] text-muted-foreground">Antic Diary</p>
            <h1 className="font-serif text-6xl mt-4 font-light">{title}</h1>
            <p className="mt-4 text-sm text-muted-foreground italic">{user.name}</p>
          </header>

          {entries.length === 0 && (
            <p className="text-center text-muted-foreground italic">
              No entries for this period.
            </p>
          )}

          {entries.map((e) => {
            const d = new Date(e.entry_date);
            return (
              <section
                key={e.entry_id}
                data-testid={`print-entry-${e.entry_id}`}
                className="print-entry mb-14 pb-10 border-b border-border last:border-0"
              >
                <div className="flex items-baseline gap-4 mb-4">
                  <span className="font-serif text-4xl font-light">
                    {d.toLocaleString("en-US", { weekday: "long", day: "numeric", month: "long" })}
                  </span>
                  {e.mood && (
                    <span className="text-xs uppercase tracking-[0.2em] text-accent">{e.mood}</span>
                  )}
                </div>
                {e.title && (
                  <h2 className="font-serif text-2xl mb-4">{e.title}</h2>
                )}
                <div
                  className="prose-diary"
                  dangerouslySetInnerHTML={{ __html: e.content || "<p><em>—</em></p>" }}
                />
                {e.tags?.length > 0 && (
                  <p className="mt-4 text-xs text-muted-foreground">
                    {e.tags.map((t) => `#${t}`).join(" · ")}
                  </p>
                )}
              </section>
            );
          })}
        </article>
      </main>
    </div>
  );
}
